import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { Button, EmptyState, Input, Paging, formatDate, formatDateTime } from "@omnabase/ui";
import { ADMIN_WORKSPACE_STATES, DEFAULT_PLANS, type AdminWorkspaceList, type AdminWorkspaceState, type Me } from "@omnabase/shared";
import { adminApi } from "../../lib/admin";
import { useNav, useSetParam } from "../../lib/nav";
import { AdminShell } from "./AdminShell";

/** /admin/workspaces (the first setup round, item 12, 2026-10-22): every workspace, newest first, 50 a page, with its owner, plan, members,
 *  real tickets, creation, one state and its last activity; a search over the name, the slug and the owner's address, and the Plan and State
 *  filters. The page, the search and the filters live in the address, and a row's page carries them back ("Back to Workspaces"). Opens on
 *  any administrator's session like every back-office read (the send-and-sections round, 2026-10-23: never the code dialog on load). Names and
 *  numbers only. */
export const STATE_LABEL: Record<AdminWorkspaceState, string> = { ok: "OK", suspended: "Suspended", mail_paused: "Mail paused", bulk_off: "Bulk sending off", ai_paused: "AI paused" };
const LIST_KEYS = ["page", "q", "plan", "state"] as const;
/** the list's own part of the address, which a workspace's page keeps to lead back */
export const listQuery = (params: URLSearchParams): string => { const n = new URLSearchParams(); for (const k of LIST_KEYS) { const v = params.get(k); if (v) n.set(k, v); } return n.toString(); };

export function AdminWorkspaces({ me }: { me: Me }) {
  const [params] = useSearchParams(); const query = listQuery(params);
  const [data, setData] = useState<AdminWorkspaceList | null>(null); const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => { try { setData(await adminApi.workspaceList(query)); setError(null); } catch (e) { setError((e as Error).message); } }, [query]);
  useEffect(() => { void load(); }, [load]);
  return (
    <AdminShell me={me} crumbs={[{ label: "Admin", href: "/admin" }, { label: "Workspaces" }]}>
      <main className="ob-page ob-settings__page" aria-label="Workspaces">
        <div className="ob-page__head"><div><h1 className="ob-page__title">Workspaces</h1><p className="ob-page__subtitle">Every workspace, newest first, with its owner, plan and state. Nothing of a ticket or a customer.</p></div></div>
        <ListTools />
        {error && <p className="ob-signin__problem" role="alert">{error}</p>}
        {data && (data.rows.length ? <ListTable data={data} back={query} /> : <EmptyState icon="search" title="No workspace matches" text="A part of the name or the slug, or the owner's address." />)}
      </main>
    </AdminShell>
  );
}

/** the search box and the two filters; a change starts again at page 1 */
function ListTools() {
  const setParam = useSetParam(); const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const [typed, setTyped] = useState(q);
  useEffect(() => setTyped(q), [q]);
  const filter = (key: "q" | "plan" | "state", value: string) => { setParam("page", null); setParam(key, value || null); };
  const submit = (e: FormEvent) => { e.preventDefault(); filter("q", typed.trim()); };
  return (
    <form className="ob-admin-list__tools" role="search" onSubmit={submit}>
      <Input className="ob-input--text" type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Name, slug or the owner's e-mail" aria-label="Search workspaces" />
      <select className="ob-select" aria-label="Plan" value={params.get("plan") ?? ""} onChange={(e) => filter("plan", e.target.value)}>
        <option value="">Any plan</option>{DEFAULT_PLANS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select className="ob-select" aria-label="State" value={params.get("state") ?? ""} onChange={(e) => filter("state", e.target.value)}>
        <option value="">Any state</option>{ADMIN_WORKSPACE_STATES.map((s) => <option key={s} value={s}>{STATE_LABEL[s]}</option>)}
      </select>
      <Button variant="primary" type="submit">Search</Button>
    </form>
  );
}

function ListTable({ data, back }: { data: AdminWorkspaceList; back: string }) {
  const nav = useNav(); const setParam = useSetParam();
  const open = (id: string) => `/admin/workspaces/${id}${back ? `?list=${encodeURIComponent(back)}` : ""}`;
  const toPage = (page: number) => setParam("page", page > 1 ? String(page) : null);
  return (
    <>
      <div className="ob-listpage__scroll ob-dtable-card"><table className="ob-dtable">
        <thead><tr><th>Name</th><th className="ob-dtable__col--phone">Owner</th><th className="ob-dtable__col--phone">Plan</th><th className="ob-dtable__num ob-dtable__col--narrow">Members</th><th className="ob-dtable__num ob-dtable__col--narrow">Tickets</th><th className="ob-dtable__col--phone">Created</th><th>State</th><th className="ob-dtable__col--narrow">Last activity</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id} className="ob-dtable__row" onClick={(e) => { if ((e.target as HTMLElement).closest("a")) return; nav(open(r.id), e as never); }}>
              <td className="ob-dtable__grow" title={`${r.name} · ${r.slug}`}><a className="ob-dtable__name" href={open(r.id)} onClick={(e) => nav(open(r.id), e)}>{r.name}</a><span className="ob-dtable__muted"> · {r.slug}</span></td>
              <td className="ob-dtable__col--phone">{r.ownerEmail ? <span className="ob-dtable__cut" title={r.ownerEmail}>{r.ownerEmail}</span> : "—"}</td>
              <td className="ob-dtable__col--phone ob-dtable__muted">{r.plan?.name ?? "—"}</td>
              <td className="ob-dtable__num ob-dtable__col--narrow ob-dtable__muted">{r.members}</td>
              <td className="ob-dtable__num ob-dtable__col--narrow ob-dtable__muted">{r.tickets}</td>
              <td className="ob-dtable__col--phone ob-dtable__muted ob-admin-list__when">{formatDate(r.createdAt)}</td>
              <td>{STATE_LABEL[r.state]}</td>
              <td className="ob-dtable__col--narrow ob-dtable__muted ob-admin-list__when">{r.lastActivityAt ? formatDateTime(r.lastActivityAt) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <Paging page={data.page} pageSize={data.pageSize} total={data.total} noun="workspaces" onPage={toPage} />
    </>
  );
}
